import { RARITY_IDS, StatUse } from "@highmarch/domain";
import type { OwnerView } from "@highmarch/app";
import { pctStep } from "../gearUi";
import { icon } from "../iconsKit";
import { gearIconUrl, uiUrl } from "../portraits";
import type { ForStrip, StripMode, UpgradeOffer } from "./armouryModel";
import type { ArmouryCtx } from "./host";
import { EMPTY_CHEST_ART } from "./itemBits";
import { ownerArt, ownerName, statShort } from "./paperDoll";
import { gearTile } from "./tile";
import { essenceIconUrl } from "../forgeArt";

/** The strip lists this many items at most (it scrolls sideways; the rest are one tap away in the Stash). */
const MAX_ITEMS = 12;

export interface StripHandlers {
  readonly onItem: (item: ForStrip["items"][number]["item"]) => void;
  readonly onStash: () => void;
  readonly onUpgrade: (offer: UpgradeOffer, card: HTMLElement) => void;
  readonly onTowers: () => void;
}

function head(title: string, link: string | null = "Stash"): string {
  return `<div class="sh"><b class="disp">${title}</b>${link ? `<button type="button" class="more">${link} ${icon("chev", 14)}</button>` : ""}</div>`;
}

function offerCard(ctx: ArmouryCtx, o: UpgradeOffer): string {
  const slot = ctx.B.gear.slots[o.item.slot];
  const [now, next] = pctStep(o.before, o.after);
  return `<button type="button" class="upc${o.affordable ? "" : " short"}" data-id="${o.item.id}">
    <span class="ic r${o.item.rarity}"><img decoding="async" src="${gearIconUrl(slot.id, RARITY_IDS[o.item.rarity])}" alt=""><i class="pl">Lv ${o.item.level}</i></span>
    <span class="tx"><b>${slot.name} → Lv ${o.item.level + 1}${o.gain > 0 ? `<i class="gain">${icon("bolt", 10)}+${o.gain}</i>` : ""}</b><span class="g">${now} → <em>${next}</em> ${statShort(o.stat)}${o.use === StatUse.Ascended ? " · tier 4" : ""}</span><span class="c"><span class="res ess${ctx.profile.forge.essence < o.cost ? " short" : ""}" aria-label="${o.cost} essence"><img decoding="async" src="${essenceIconUrl()}" alt="">${o.cost}</span></span></span>
  </button>`;
}

/**
 * The strip under the paper doll: the "For X" items (best gain first; tap → item card), or — once the stash has
 * nothing better for this owner — "Get stronger": its best one-level raises (most gear per essence) and the tower-level
 * route (the Towers tab). Also its empty and locked states.
 */
export function renderStrip(host: HTMLElement, ctx: ArmouryCtx, v: OwnerView, strip: ForStrip, mode: StripMode, offers: readonly UpgradeOffer[], h: StripHandlers): void {
  const name = ownerName(v);
  host.className = `arm-for m-${mode}`;
  if (mode === "locked") { host.innerHTML = head(`${name} is locked`, null); return; }
  if (mode === "empty") {
    host.innerHTML = `<div class="emptyrow"><img decoding="async" src="${uiUrl(EMPTY_CHEST_ART)}" alt=""><b class="disp">No gear yet</b></div>`;
    return;
  }
  if (mode === "items") {
    host.innerHTML = `${head(`For ${name}`)}<div class="row"></div>`;
    const row = host.querySelector<HTMLElement>(".row")!;
    for (const f of strip.items.slice(0, MAX_ITEMS)) {
      const t = gearTile(ctx, f.item, { delta: f.delta, wornChip: true, isNew: f.isNew, size: "m" });
      t.onclick = () => h.onItem(f.item);
      row.append(t);
    }
  } else {
    const towerArt = ownerArt(v);
    const tower = `<button type="button" class="upc tower"><span class="ic face">${towerArt ? `<img decoding="async" src="${towerArt}" alt="">` : icon("tower", 24)}</span><span class="tx"><b>${name} → Lv ${v.level + 1}</b><span class="c go">Towers ${icon("chev", 12)}</span></span></button>`;
    host.innerHTML = `${head("Get stronger")}<div class="row">${offers.map((o) => offerCard(ctx, o)).join("")}${tower}</div>`;
    for (const card of host.querySelectorAll<HTMLElement>(".upc[data-id]")) {
      const offer = offers.find((o) => String(o.item.id) === card.dataset.id)!;
      card.onclick = () => h.onUpgrade(offer, card);
    }
    host.querySelector<HTMLButtonElement>(".upc.tower")!.onclick = () => h.onTowers();
  }
  host.querySelector<HTMLButtonElement>(".sh .more")?.addEventListener("click", () => h.onStash());
}
